import { Badge, Group, Image, Paper, SimpleGrid, Stack, Text } from "@mantine/core"
import { useTranslation } from "react-i18next"
import { DetailSection, InfoRow } from "../../../components/crud"
import { EmptyValue } from "../utils"
import type { AgencyDetail } from "../models/agency"
import { STATUS_MAP, DISTRICTS_PT, DISTRICTS_BR } from "../models/agency"
import { env } from "../../../env"

interface Props {
  agency: AgencyDetail
}

function districtLabel(code: number | null): string {
  if (code == null) return ""
  const districts = env.isGeaBrasil ? DISTRICTS_BR : DISTRICTS_PT
  return districts.find((d) => d.value === code)?.label ?? ""
}

export default function AgencyDetailContent({ agency }: Props) {
  const { t } = useTranslation()
  const statusInfo = STATUS_MAP[agency.status]

  return (
    <Stack p="md" gap="md">
      <DetailSection title={t("agencies.generalInfo")}>
        <SimpleGrid cols={2} spacing="sm" verticalSpacing="sm">
          <InfoRow label={t("agencies.name")}>
            <Text size="sm" fw={500}>{agency.name}</Text>
          </InfoRow>
          <InfoRow label={t("agencies.socialDenomination")}>
            <Text size="sm">{agency.social_denomination ?? <EmptyValue />}</Text>
          </InfoRow>
          <InfoRow label={t("agencies.nif")}>
            <Text size="sm">{agency.nif}</Text>
          </InfoRow>
          <InfoRow label={t("agencies.RNAVT")}>
            <Text size="sm">{agency.RNAVT}</Text>
          </InfoRow>
          <InfoRow label={t("agencies.district")}>
            <Text size="sm">{districtLabel(agency.district)}</Text>
          </InfoRow>
          <InfoRow label={t("agencies.status")}>
            <Badge color={statusInfo?.color ?? "gray"} variant="light" size="sm">
              {t(`agencies.${statusInfo?.label ?? "inactive"}`)}
            </Badge>
          </InfoRow>
        </SimpleGrid>
      </DetailSection>

      <DetailSection title={t("agencies.contactInfo")}>
        <SimpleGrid cols={2} spacing="sm" verticalSpacing="sm">
          <InfoRow label={t("agencies.address")}>
            <Text size="sm">{agency.address}</Text>
          </InfoRow>
          <InfoRow label={t("agencies.postalCode")}>
            <Text size="sm">{agency.postal_code}</Text>
          </InfoRow>
          <InfoRow label={t("agencies.locale")}>
            <Text size="sm">{agency.locale}</Text>
          </InfoRow>
          <InfoRow label={t("agencies.phone")}>
            <Text size="sm">{agency.phone_number ?? <EmptyValue />}</Text>
          </InfoRow>
          <InfoRow label={t("agencies.email")}>
            <Text size="sm">{agency.email}</Text>
          </InfoRow>
        </SimpleGrid>
      </DetailSection>

      <DetailSection title={t("agencies.network")}>
        <SimpleGrid cols={2} spacing="sm" verticalSpacing="sm">
          <InfoRow label={t("agencies.type")}>
            <Badge color={agency.is_master ? "blue" : "gray"} variant="light" size="sm">
              {agency.is_master ? t("agencies.headOffice") : t("agencies.branch")}
            </Badge>
          </InfoRow>
          {!agency.is_master && (
            <InfoRow label={t("agencies.masterAgency")}>
              <Text size="sm">{agency.master_agency?.name ?? <EmptyValue />}</Text>
            </InfoRow>
          )}
          <InfoRow label={t("agencies.subdomain")}>
            <Text size="sm">{agency.subdomain ?? <EmptyValue />}</Text>
          </InfoRow>
        </SimpleGrid>
      </DetailSection>

      <DetailSection title={t("agencies.socialOnline")}>
        <SimpleGrid cols={2} spacing="sm" verticalSpacing="sm">
          <InfoRow label="Facebook">
            {agency.facebook_url
              ? <Text size="sm" component="a" href={agency.facebook_url} target="_blank" rel="noopener noreferrer">{agency.facebook_url}</Text>
              : <EmptyValue />}
          </InfoRow>
          <InfoRow label="Instagram">
            {agency.instagram_url
              ? <Text size="sm" component="a" href={agency.instagram_url} target="_blank" rel="noopener noreferrer">{agency.instagram_url}</Text>
              : <EmptyValue />}
          </InfoRow>
          <InfoRow label={t("agencies.color")}>
            {agency.color
              ? <Group gap="xs"><div style={{ width: 16, height: 16, borderRadius: 4, backgroundColor: agency.color }} /><Text size="sm">{agency.color}</Text></Group>
              : <EmptyValue />}
          </InfoRow>
        </SimpleGrid>
      </DetailSection>

      <DetailSection title={t("agencies.internalInfo")}>
        <SimpleGrid cols={2} spacing="sm" verticalSpacing="sm">
          <InfoRow label={t("agencies.bdEgoi")}>
            <Text size="sm">{agency.bd_egoi ?? <EmptyValue />}</Text>
          </InfoRow>
          <InfoRow label={t("agencies.accessionDate")}>
            <Text size="sm">{agency.accession_date ?? <EmptyValue />}</Text>
          </InfoRow>
          <InfoRow label={t("agencies.representative")}>
            <Text size="sm">{agency.representative ?? <EmptyValue />}</Text>
          </InfoRow>
        </SimpleGrid>
      </DetailSection>

      {agency.medias.length > 0 && (
        <DetailSection title={t("agencies.files")}>
          <Stack gap="xs">
            {agency.medias.map((m) => (
              <Group key={m.id} gap="xs">
                <i className={`fa ${m.mime_type?.includes("image") ? "fa-file-image-o" : "fa-file-o"}`} />
                <Text size="sm" component="a" href={m.url} target="_blank" rel="noopener noreferrer">{m.real_name}</Text>
              </Group>
            ))}
          </Stack>
        </DetailSection>
      )}

      {(agency.logo_url || agency.facebook_qrcode_path || agency.subdomain_qrcode_path || agency.instagram_qrcode_path) && (
        <DetailSection title={t("agencies.multimedia")}>
          <SimpleGrid cols={2} spacing="md">
            {agency.logo_url && (
              <Paper withBorder p="xs" radius="md">
                <Text size="xs" c="dimmed" mb={4}>{t("agencies.logo")}</Text>
                <Image src={agency.logo_url} alt="Logo" h={100} fit="contain" />
              </Paper>
            )}
            {agency.facebook_qrcode_path && (
              <Paper withBorder p="xs" radius="md">
                <Text size="xs" c="dimmed" mb={4}>QR Facebook</Text>
                <Image src={`/img/agencies/${agency.facebook_qrcode_path}`} alt="QR Facebook" h={80} fit="contain" />
              </Paper>
            )}
            {agency.subdomain_qrcode_path && (
              <Paper withBorder p="xs" radius="md">
                <Text size="xs" c="dimmed" mb={4}>QR Subdomínio</Text>
                <Image src={`/img/agencies/${agency.subdomain_qrcode_path}`} alt="QR Subdomínio" h={80} fit="contain" />
              </Paper>
            )}
            {agency.instagram_qrcode_path && (
              <Paper withBorder p="xs" radius="md">
                <Text size="xs" c="dimmed" mb={4}>QR Instagram</Text>
                <Image src={`/img/agencies/${agency.instagram_qrcode_path}`} alt="QR Instagram" h={80} fit="contain" />
              </Paper>
            )}
          </SimpleGrid>
        </DetailSection>
      )}
    </Stack>
  )
}
